import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { orderAPI } from '../../utils/api';
import { toast } from 'sonner';
import axios from 'axios';
import { RefreshCw, Printer, Loader2, Bike, ShoppingBag, ChefHat, Check, Truck, CircleSlash, PackageCheck } from 'lucide-react';
import printerService from '../../services/printer/printerService';
import { markPrintedLocally } from '../../utils/autoPrint';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const gbp = (n) => `£${Number(n || 0).toFixed(2)}`;

const orderNumber = (o) =>
  o.daily_order_number
    ? `#${String(o.daily_order_number).padStart(3, '0')}`
    : `#${(o.id || '').slice(-6).toUpperCase()}`;

const timeLabel = (o) =>
  new Date(o.created_at).toLocaleTimeString('en-GB', {
    hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London',
  });

const STATUS_ORDER = ['pending', 'received', 'confirmed', 'preparing', 'ready', 'out_for_delivery', 'completed'];
const STATUS_LABEL = {
  pending: 'New',
  received: 'Received',
  confirmed: 'Confirmed',
  preparing: 'Preparing',
  ready: 'Ready',
  out_for_delivery: 'Out for delivery',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

/**
 * POS Orders — today's ticket list + tap-to-view detail.
 *
 * Deliberately lean: no filters, no CSV, no history. Cashier only needs to
 * see the queue and advance status. Full order management lives on
 * /admin/orders in the web app.
 */
export default function PosOrdersPage() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [updating, setUpdating] = useState(null);
  const [printing, setPrinting] = useState(null);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await orderAPI.getAllOrders();
      setOrders(res.data || []);
    } catch (_err) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
    const id = setInterval(fetchOrders, 10000);
    return () => clearInterval(id);
  }, [fetchOrders]);

  // Filter to today only (POS is a service-time device — history is admin-side)
  const todayOrders = useMemo(() => {
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Europe/London', year: 'numeric', month: '2-digit', day: '2-digit',
    });
    const today = fmt.format(new Date());
    return (orders || [])
      .filter((o) => fmt.format(new Date(o.created_at)) === today)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [orders]);

  const advanceStatus = async (order, targetStatus) => {
    setUpdating(order.id);
    try {
      await orderAPI.updateOrderStatus(order.id, targetStatus);
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, order_status: targetStatus } : o)));
      if (selected?.id === order.id) setSelected((prev) => ({ ...prev, order_status: targetStatus }));
      toast.success(`Marked ${STATUS_LABEL[targetStatus]}`);
    } catch (_err) {
      toast.error('Failed to update');
    } finally {
      setUpdating(null);
    }
  };

  const reprint = async (order) => {
    setPrinting(order.id);
    const result = await printerService.printOrder(order);
    if (result.success) {
      markPrintedLocally(order.id);
      const token = localStorage.getItem('token');
      await axios
        .post(`${API_URL}/api/admin/orders/${order.id}/qz-print`, { success: true }, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .catch(() => {});
      toast.success('Receipt printed');
    } else {
      toast.error(`Print failed: ${result.error || 'unknown error'}`);
    }
    setPrinting(null);
  };

  return (
    <div className="p-4" data-testid="pos-orders-page">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-xl font-bold">Today&apos;s Orders</h2>
        <button
          type="button"
          onClick={fetchOrders}
          className="p-2 rounded-full hover:bg-neutral-200 active:bg-neutral-300"
          data-testid="pos-orders-refresh"
          aria-label="Refresh"
        >
          <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-neutral-500">
          <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
        </div>
      ) : todayOrders.length === 0 ? (
        <div className="text-center py-16 text-neutral-500" data-testid="pos-orders-empty">
          No orders today yet.
        </div>
      ) : (
        <div className="space-y-2">
          {todayOrders.map((o) => {
            const kindIcon = o.order_type === 'delivery' ? Bike : ShoppingBag;
            const Icon = kindIcon;
            return (
              <button
                type="button"
                key={o.id}
                onClick={() => setSelected(o)}
                className="w-full text-left p-3 rounded-lg bg-white border border-neutral-200 hover:border-neutral-300 active:bg-neutral-50"
                data-testid={`pos-order-card-${o.id}`}
              >
                <div className="flex items-center justify-between">
                  <div className="font-semibold">
                    {orderNumber(o)} · {o.customer_name}
                  </div>
                  <div className="text-sm font-bold">{gbp(o.total_amount)}</div>
                </div>
                <div className="flex items-center justify-between mt-1 text-xs text-neutral-500">
                  <div className="flex items-center gap-1">
                    <Icon className="h-3 w-3" />
                    <span className="capitalize">{o.order_type}</span>
                    <span>·</span>
                    <span>{timeLabel(o)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {o.print_status === 'FAILED' && (
                      <span
                        className="px-1.5 py-0.5 rounded bg-red-100 text-red-800 font-semibold"
                        data-testid={`pos-order-print-failed-${o.id}`}
                      >
                        Print failed
                      </span>
                    )}
                    {o.print_status === 'PRINTED' && (
                      <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700">
                        Printed
                      </span>
                    )}
                    <span
                      className={`px-2 py-0.5 rounded-full font-medium ${
                        o.order_status === 'pending'
                          ? 'bg-yellow-100 text-yellow-800'
                          : o.order_status === 'completed'
                          ? 'bg-emerald-100 text-emerald-800'
                          : o.order_status === 'cancelled'
                          ? 'bg-neutral-200 text-neutral-700'
                          : 'bg-blue-100 text-blue-800'
                      }`}
                    >
                      {STATUS_LABEL[o.order_status] || o.order_status}
                    </span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Ticket detail modal */}
      {selected && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center"
          onClick={() => setSelected(null)}
          data-testid="pos-order-detail-backdrop"
        >
          <div
            className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-xl p-5 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="pos-order-detail"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="text-xl font-bold">{orderNumber(selected)}</div>
                <div className="text-sm text-neutral-500">
                  {selected.customer_name} · {selected.customer_phone}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="text-neutral-500 hover:text-neutral-900 text-sm"
                data-testid="pos-order-detail-close"
              >
                Close
              </button>
            </div>

            <div className="text-xs text-neutral-500 mb-2">
              {selected.order_type === 'delivery' ? 'Delivery' : 'Collection'} · {timeLabel(selected)} · {gbp(selected.total_amount)}
            </div>

            {/* Items */}
            <div className="border-t border-b border-neutral-200 py-2 mb-3">
              {(selected.items || []).map((it, i) => (
                <div key={i} className="text-sm flex justify-between py-1">
                  <span>{it.quantity} × {it.name}</span>
                  <span>{gbp((it.price || 0) * (it.quantity || 1))}</span>
                </div>
              ))}
            </div>

            {selected.notes && (
              <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded text-sm">
                <div className="font-semibold text-xs text-amber-800 mb-0.5">Notes</div>
                {selected.notes}
              </div>
            )}

            {/* Status actions */}
            <div className="grid grid-cols-2 gap-2 mb-3">
              {STATUS_ORDER
                .filter((s) => s !== selected.order_status)
                .slice(0, 4)
                .map((s) => {
                  const map = { confirmed: Check, preparing: ChefHat, ready: PackageCheck, out_for_delivery: Truck, completed: PackageCheck };
                  const Ic = map[s] || Check;
                  return (
                    <button
                      key={s}
                      type="button"
                      onClick={() => advanceStatus(selected, s)}
                      disabled={updating === selected.id}
                      className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-neutral-900 text-white text-sm font-medium hover:bg-neutral-800 active:bg-neutral-700 disabled:opacity-50"
                      data-testid={`pos-status-btn-${s}`}
                    >
                      <Ic className="h-4 w-4" /> {STATUS_LABEL[s]}
                    </button>
                  );
                })}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => reprint(selected)}
                disabled={printing === selected.id}
                className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-white border border-neutral-300 text-sm font-medium hover:bg-neutral-50 disabled:opacity-50"
                data-testid="pos-order-reprint"
              >
                {printing === selected.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                Reprint
              </button>
              <button
                type="button"
                onClick={() => advanceStatus(selected, 'cancelled')}
                disabled={updating === selected.id || selected.order_status === 'cancelled'}
                className="flex items-center justify-center gap-1.5 py-2.5 rounded-lg bg-white border border-red-300 text-red-700 text-sm font-medium hover:bg-red-50 disabled:opacity-50"
                data-testid="pos-order-cancel"
              >
                <CircleSlash className="h-4 w-4" /> Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
